import type { TargetedPointerEvent } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import type { VirtualInput } from '#contract'
import styles from './joystick.module.css'

/** Base radius in CSS pixels: the knob's full deflection. */
const RADIUS = 56
/** Deflection at which the stick also holds sprint, so there's no sprint button. */
const AUTO_SPRINT = 0.9

interface Gesture {
  readonly pointerId: number
  readonly originX: number
  readonly originY: number
}

/** Where the base sits in the zone and how far the knob is pushed, in CSS pixels. */
interface Stick {
  readonly x: number
  readonly y: number
  readonly knobX: number
  readonly knobY: number
}

/** A floating joystick: the base appears under the thumb, anywhere in its zone. */
export function Joystick({ touch }: { readonly touch: VirtualInput }) {
  const [stick, setStick] = useState<Stick | null>(null)
  const gestureRef = useRef<Gesture | null>(null)
  const sprintingRef = useRef(false)

  const setSprint = (sprint: boolean): void => {
    if (sprint === sprintingRef.current) return
    sprintingRef.current = sprint
    touch.setButton('sprint', sprint)
  }

  // Hidden mid-gesture (another device took over): let go of everything.
  useEffect(
    () => () => {
      touch.setMove(0, 0)
      touch.setButton('sprint', false)
    },
    [touch],
  )

  const onDown = (event: TargetedPointerEvent<HTMLDivElement>): void => {
    if (gestureRef.current) return
    event.currentTarget.setPointerCapture(event.pointerId)
    const zone = event.currentTarget.getBoundingClientRect()
    gestureRef.current = {
      pointerId: event.pointerId,
      originX: event.clientX,
      originY: event.clientY,
    }
    setStick({ x: event.clientX - zone.left, y: event.clientY - zone.top, knobX: 0, knobY: 0 })
  }

  const onMove = (event: TargetedPointerEvent<HTMLDivElement>): void => {
    const current = gestureRef.current
    if (current?.pointerId !== event.pointerId) return
    const dx = event.clientX - current.originX
    const dy = event.clientY - current.originY
    const length = Math.hypot(dx, dy)
    const scale = length > RADIUS ? RADIUS / length : 1
    const knobX = dx * scale
    const knobY = dy * scale
    setStick((previous) => previous && { ...previous, knobX, knobY })
    // Screen y grows downward; forward is up.
    touch.setMove(knobX / RADIUS, -knobY / RADIUS)
    setSprint(length / RADIUS >= AUTO_SPRINT)
  }

  const onEnd = (event: TargetedPointerEvent<HTMLDivElement>): void => {
    if (gestureRef.current?.pointerId !== event.pointerId) return
    gestureRef.current = null
    setStick(null)
    touch.setMove(0, 0)
    setSprint(false)
  }

  return (
    <div
      class={styles.zone}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onEnd}
      onPointerCancel={onEnd}
    >
      {stick && (
        <div
          class={styles.base}
          // Preact 11 doesn't add `px` to numbers.
          style={{
            left: `${String(stick.x)}px`,
            top: `${String(stick.y)}px`,
            width: `${String(RADIUS * 2)}px`,
            height: `${String(RADIUS * 2)}px`,
          }}
        >
          <div
            class={styles.knob}
            style={{ transform: `translate(${String(stick.knobX)}px, ${String(stick.knobY)}px)` }}
          />
        </div>
      )}
    </div>
  )
}
